import { useState } from "react";
import { motion } from "framer-motion";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ArrowUpRight, Loader2, FileUp, X } from "lucide-react";
import { api, fileToBase64 } from "@/lib/api";

const inputClass =
  "w-full input-line-light py-3 text-[15px] placeholder:text-[#0A0A0A]/30";
const labelClass =
  "block text-[10px] tracking-[0.22em] uppercase font-semibold text-[#1B3B36] mb-2";

function AppointmentForm() {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    company: "",
    preferred_date: "",
    preferred_time: "",
    topic: "corporate",
    notes: "",
  });

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.topic) {
      toast.error("Compila i campi obbligatori");
      return;
    }
    setLoading(true);
    try {
      await api.post("/appointments", form);
      toast.success(
        "Richiesta inviata. Ti contatteremo per confermare l'appuntamento."
      );
      setForm({
        full_name: "",
        email: "",
        phone: "",
        company: "",
        preferred_date: "",
        preferred_time: "",
        topic: "corporate",
        notes: "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="grid sm:grid-cols-2 gap-x-8 gap-y-7"
      data-testid="appointment-form"
    >
      <div>
        <label className={labelClass}>Nome e Cognome *</label>
        <input
          required
          value={form.full_name}
          onChange={handle("full_name")}
          className={inputClass}
          placeholder="Mario Rossi"
          data-testid="appt-full-name"
        />
      </div>
      <div>
        <label className={labelClass}>Email *</label>
        <input
          required
          type="email"
          value={form.email}
          onChange={handle("email")}
          className={inputClass}
          placeholder="m.rossi@azienda.it"
          data-testid="appt-email"
        />
      </div>
      <div>
        <label className={labelClass}>Telefono</label>
        <input
          value={form.phone}
          onChange={handle("phone")}
          className={inputClass}
          placeholder="+39 ..."
          data-testid="appt-phone"
        />
      </div>
      <div>
        <label className={labelClass}>Società</label>
        <input
          value={form.company}
          onChange={handle("company")}
          className={inputClass}
          placeholder="Ragione sociale"
          data-testid="appt-company"
        />
      </div>
      <div>
        <label className={labelClass}>Data preferita</label>
        <input
          type="date"
          value={form.preferred_date}
          onChange={handle("preferred_date")}
          className={inputClass}
          data-testid="appt-date"
        />
      </div>
      <div>
        <label className={labelClass}>Orario preferito</label>
        <input
          type="time"
          value={form.preferred_time}
          onChange={handle("preferred_time")}
          className={inputClass}
          data-testid="appt-time"
        />
      </div>
      <div className="sm:col-span-2">
        <label className={labelClass}>Area di interesse *</label>
        <select
          required
          value={form.topic}
          onChange={handle("topic")}
          className={`${inputClass} bg-transparent`}
          data-testid="appt-topic"
        >
          <option value="corporate">Corporate & Business</option>
          <option value="real_estate">Real Estate</option>
          <option value="ma">M&A · Operazioni straordinarie</option>
          <option value="governance">Governance & Soci</option>
          <option value="other">Altro</option>
        </select>
      </div>
      <div className="sm:col-span-2">
        <label className={labelClass}>Breve descrizione</label>
        <textarea
          rows={4}
          value={form.notes}
          onChange={handle("notes")}
          className={`${inputClass} resize-none`}
          placeholder="Descrivi sinteticamente l'esigenza..."
          data-testid="appt-notes"
        />
      </div>

      <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-4">
        <p className="text-xs text-[#0A0A0A]/60 max-w-md leading-relaxed">
          Il primo incontro è{" "}
          <span className="text-[#1B3B36] font-semibold">gratuito</span> e ha
          durata massima di 30 minuti. Per il prosieguo verrà fornito un
          preventivo dedicato.
        </p>
        <button
          type="submit"
          disabled={loading}
          className="btn-primary inline-flex items-center gap-3 px-7 py-4 text-[12px] font-semibold tracking-[0.2em] uppercase disabled:opacity-60"
          data-testid="appt-submit"
        >
          {loading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <ArrowUpRight size={16} />
          )}
          Prenota il confronto
        </button>
      </div>
    </form>
  );
}

function OpinionForm() {
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState([]);
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    company: "",
    area: "corporate",
    subject: "",
    description: "",
    urgency: "standard",
  });

  const handle = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFile = async (e) => {
    const f = Array.from(e.target.files || []);
    const oversize = f.find((x) => x.size > 5 * 1024 * 1024);
    if (oversize) {
      toast.error("Ogni file deve essere max 5MB");
      return;
    }
    setFiles((prev) => [...prev, ...f].slice(0, 5));
    e.target.value = "";
  };

  const removeFile = (idx) =>
    setFiles((prev) => prev.filter((_, i) => i !== idx));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.subject || !form.description) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }
    setLoading(true);
    try {
      const attachments = await Promise.all(files.map((f) => fileToBase64(f)));
      await api.post("/opinions", { ...form, attachments });
      toast.success(
        "Richiesta ricevuta. Ti contatteremo con un preventivo dedicato."
      );
      setForm({
        full_name: "",
        email: "",
        phone: "",
        company: "",
        area: "corporate",
        subject: "",
        description: "",
        urgency: "standard",
      });
      setFiles([]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      className="grid sm:grid-cols-2 gap-x-8 gap-y-7"
      data-testid="opinion-form"
    >
      <div>
        <label className={labelClass}>Nome e Cognome *</label>
        <input
          required
          value={form.full_name}
          onChange={handle("full_name")}
          className={inputClass}
          placeholder="Mario Rossi"
          data-testid="op-full-name"
        />
      </div>
      <div>
        <label className={labelClass}>Email *</label>
        <input
          required
          type="email"
          value={form.email}
          onChange={handle("email")}
          className={inputClass}
          placeholder="m.rossi@azienda.it"
          data-testid="op-email"
        />
      </div>
      <div>
        <label className={labelClass}>Telefono</label>
        <input
          value={form.phone}
          onChange={handle("phone")}
          className={inputClass}
          data-testid="op-phone"
        />
      </div>
      <div>
        <label className={labelClass}>Società</label>
        <input
          value={form.company}
          onChange={handle("company")}
          className={inputClass}
          data-testid="op-company"
        />
      </div>
      <div>
        <label className={labelClass}>Area *</label>
        <select
          value={form.area}
          onChange={handle("area")}
          className={`${inputClass} bg-transparent`}
          data-testid="op-area"
        >
          <option value="corporate">Corporate & Business</option>
          <option value="real_estate">Real Estate</option>
          <option value="other">Altro</option>
        </select>
      </div>
      <div>
        <label className={labelClass}>Urgenza</label>
        <select
          value={form.urgency}
          onChange={handle("urgency")}
          className={`${inputClass} bg-transparent`}
          data-testid="op-urgency"
        >
          <option value="standard">Standard</option>
          <option value="urgent">Urgente</option>
        </select>
      </div>
      <div className="sm:col-span-2">
        <label className={labelClass}>Oggetto del parere *</label>
        <input
          required
          value={form.subject}
          onChange={handle("subject")}
          className={inputClass}
          placeholder="Es. Patto parasociale e clausole di exit"
          data-testid="op-subject"
        />
      </div>
      <div className="sm:col-span-2">
        <label className={labelClass}>Descrizione del caso *</label>
        <textarea
          required
          rows={5}
          value={form.description}
          onChange={handle("description")}
          className={`${inputClass} resize-none`}
          placeholder="Contesto, parti coinvolte, obiettivo della consulenza..."
          data-testid="op-description"
        />
      </div>

      <div className="sm:col-span-2">
        <label className={labelClass}>Documentazione (max 5 file · 5MB)</label>
        <label
          htmlFor="op-files"
          className="flex items-center gap-3 border border-dashed border-[#0A0A0A]/20 hover:border-[#1B3B36] transition-colors cursor-pointer py-5 px-5 text-sm text-[#0A0A0A]/65"
          data-testid="op-files-dropzone"
        >
          <FileUp size={16} className="text-[#1B3B36]" />
          Trascina qui o seleziona i documenti rilevanti
          <input
            id="op-files"
            type="file"
            multiple
            onChange={onFile}
            className="hidden"
            data-testid="op-files-input"
          />
        </label>
        {files.length > 0 && (
          <ul className="mt-4 space-y-2">
            {files.map((f, i) => (
              <li
                key={i}
                className="flex items-center justify-between bg-white border border-[#0A0A0A]/10 px-4 py-2 text-xs text-[#0A0A0A]/80"
              >
                <span className="truncate">
                  {f.name}{" "}
                  <span className="text-[#0A0A0A]/40">
                    · {Math.round(f.size / 1024)} KB
                  </span>
                </span>
                <button
                  type="button"
                  onClick={() => removeFile(i)}
                  className="text-[#0A0A0A]/50 hover:text-[#1B3B36]"
                  data-testid={`op-file-remove-${i}`}
                >
                  <X size={14} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="sm:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 pt-4">
        <p className="text-xs text-[#0A0A0A]/60 max-w-md leading-relaxed">
          Riceverai un preventivo personalizzato. Una volta accettato, potrai
          procedere al pagamento online tramite il link dedicato.
        </p>
        <button
          type="submit"
          disabled={loading}
          className="btn-primary inline-flex items-center gap-3 px-7 py-4 text-[12px] font-semibold tracking-[0.2em] uppercase disabled:opacity-60"
          data-testid="op-submit"
        >
          {loading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <ArrowUpRight size={16} />
          )}
          Invia richiesta parere
        </button>
      </div>
    </form>
  );
}

export default function EngageForms() {
  return (
    <section
      id="engage"
      className="relative py-28 lg:py-36 border-t border-[#FAF9F6]/10 overflow-hidden"
      data-testid="engage-section"
    >
      <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid lg:grid-cols-12 gap-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="lg:col-span-4"
          >
            <div className="overline mb-5">Servizi Legali</div>
            <h2 className="font-display text-4xl lg:text-5xl font-medium leading-[1.02] tracking-tight">
              Consulto legale
              <span className="block text-[#FAF9F6]/50 italic font-normal">
                o parere online.
              </span>
            </h2>
            <p className="mt-6 text-[15px] text-[#FAF9F6]/65 leading-relaxed">
              Prenota un primo consulto legale gratuito di 30 minuti per
              inquadrare l&apos;operazione o la controversia, oppure richiedi
              un parere legale strutturato online con preventivo dedicato.
            </p>

            <div className="mt-10 pt-8 border-t border-[#FAF9F6]/10 space-y-5 text-sm">
              <div>
                <div className="overline text-[10px] mb-1">Primo incontro</div>
                <p className="text-[#FAF9F6]/75">
                  Gratuito · 30 minuti · Valutazione preliminare delle
                  criticità.
                </p>
              </div>
              <div>
                <div className="overline text-[10px] mb-1">
                  Parere online
                </div>
                <p className="text-[#FAF9F6]/75">
                  Quotazione su misura in base a complessità ed urgenza del
                  caso.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="lg:col-span-8 bg-[#EAECE7] text-[#0A0A0A] border border-[#0A0A0A]/12 p-8 lg:p-12"
          >
            <Tabs defaultValue="appointment" className="w-full">
              <TabsList
                className="bg-transparent border-b border-[#0A0A0A]/10 rounded-none p-0 h-auto mb-8 w-full justify-start gap-8"
                data-testid="engage-tabs"
              >
                <TabsTrigger
                  value="appointment"
                  className="rounded-none bg-transparent border-b-2 border-transparent data-[state=active]:border-[#1B3B36] data-[state=active]:bg-transparent data-[state=active]:text-[#1B3B36] px-0 pb-4 text-[12px] tracking-[0.22em] uppercase font-semibold text-[#0A0A0A]/50 hover:text-[#0A0A0A]"
                  data-testid="tab-appointment"
                >
                  Prenota appuntamento
                </TabsTrigger>
                <TabsTrigger
                  value="opinion"
                  className="rounded-none bg-transparent border-b-2 border-transparent data-[state=active]:border-[#1B3B36] data-[state=active]:bg-transparent data-[state=active]:text-[#1B3B36] px-0 pb-4 text-[12px] tracking-[0.22em] uppercase font-semibold text-[#0A0A0A]/50 hover:text-[#0A0A0A]"
                  data-testid="tab-opinion"
                >
                  Richiedi parere online
                </TabsTrigger>
              </TabsList>
              <TabsContent value="appointment" className="mt-0">
                <AppointmentForm />
              </TabsContent>
              <TabsContent value="opinion" className="mt-0">
                <OpinionForm />
              </TabsContent>
            </Tabs>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
